import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, map, skip, startWith, takeUntil } from 'rxjs';
import { ShiftStatusConst } from '../../shared/constant';
import { IShift } from '../../shared/interfaces';
import { ProfessionalService } from '../../shared/services/professional.service';
import { IShiftDashboardTodayStats, ShiftsService } from '../../shared/services/shifts.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  protected readonly ShiftStatusConst = ShiftStatusConst;

  today = new Date();
  loading = true;
  lastUpdatedAt?: Date;

  stats = {
    todayTotal: 0,
    todayOpen: 0,
    inProgress: 0,
    cancelled: 0,
    noShow: 0,
    coveragePct: 0,
    professionals: 0,
  };

  review = {
    professionalsPending: 0,
    professionalsRejected: 0,
    filesPending: 0,
    filesPendingUpload: 0,
    professionalsWithPendingFiles: 0,
  };

  nextShifts: Array<{
    start: Date;
    end: Date;
    hospital?: string;
    department?: string;
    professional?: string;
    status: ShiftStatusConst;
  }> = [];

  alerts: Array<{
    title: string;
    description: string;
    level: 'info' | 'warning' | 'danger';
  }> = [];

  private readonly destroy$ = new Subject<void>();
  private statsLoaded = false;
  private shiftsLoaded = false;
  private reviewLoaded = false;

  constructor(
    private readonly shiftsService: ShiftsService,
    private readonly professionalService: ProfessionalService
  ) {}

  ngOnInit(): void {
    this.shiftsService.shifts$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe((shifts) => {
        this.recomputeFromShifts(shifts);
        this.shiftsLoaded = true;
        this.tryFinishLoading();
      });

    this.professionalService.professionals$
      .pipe(
        map((professionals: any[] | null) => professionals?.length ?? 0),
        startWith(0),
        takeUntil(this.destroy$)
      )
      .subscribe((count) => {
        this.stats.professionals = count;
      });

    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    this.today = new Date();
    this.statsLoaded = false;
    this.shiftsLoaded = false;
    this.reviewLoaded = false;

    const start = this.formatDateForApi(this.addDays(new Date(), -1));
    const end = this.formatDateForApi(this.addDays(new Date(), 7));

    this.shiftsService.getDashboardTodayStats().pipe(takeUntil(this.destroy$)).subscribe({
      next: (stats: IShiftDashboardTodayStats) => {
        this.stats.todayTotal = stats.todayTotal;
        this.stats.todayOpen = stats.todayOpen;
        this.stats.coveragePct = stats.coveragePct;
        this.stats.inProgress = stats.inProgress;
        this.stats.cancelled = stats.cancelled;
        this.stats.noShow = stats.noShow;
        this.statsLoaded = true;
        this.tryFinishLoading();
      },
      error: () => {
        this.statsLoaded = true;
        this.tryFinishLoading();
      },
    });

    this.professionalService.getDashboardSummary().pipe(takeUntil(this.destroy$)).subscribe({
      next: (summary: any) => {
        this.review.professionalsPending = summary?.professionals?.pending ?? 0;
        this.review.professionalsRejected = summary?.professionals?.rejected ?? 0;
        this.review.filesPending = summary?.files?.pending ?? 0;
        this.review.filesPendingUpload = summary?.files?.pendingUpload ?? 0;
        this.review.professionalsWithPendingFiles = summary?.files?.professionalsWithPendingFiles ?? 0;
        this.reviewLoaded = true;
        this.tryFinishLoading();
      },
      error: () => {
        this.reviewLoaded = true;
        this.tryFinishLoading();
      },
    });

    this.shiftsService.loadShifts(start, end);
    this.professionalService.getAllProfessionals();
  }

  statusLabel(status: ShiftStatusConst): string {
    switch (status) {
      case ShiftStatusConst.AVAILABLE:
        return 'Aberto';
      case ShiftStatusConst.OFFERED:
        return 'Ofertado';
      case ShiftStatusConst.ASSIGNED:
        return 'Atribuído';
      case ShiftStatusConst.IN_PROGRESS:
        return 'Em andamento';
      case ShiftStatusConst.COMPLETED:
        return 'Concluído';
      case ShiftStatusConst.CANCELLED:
        return 'Cancelado';
      case ShiftStatusConst.NO_SHOW:
        return 'Falta';
      case ShiftStatusConst.INCOMPLETE:
        return 'Incompleto';
      default:
        return status;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryFinishLoading(): void {
    if (!this.statsLoaded || !this.shiftsLoaded || !this.reviewLoaded) return;
    this.loading = false;
    this.lastUpdatedAt = new Date();
  }

  private recomputeFromShifts(shifts: IShift[]): void {
    const now = new Date();
    const todayKey = this.dayKey(now);

    const shiftsWithTimes = (shifts ?? []).map((shift) => ({
      shift,
      start: this.shiftStartDateTime(shift),
      end: this.shiftEndDateTime(shift),
    }));

    const today = shiftsWithTimes.filter((s) => this.dayKey(s.start) === todayKey);
    const todayActive = today.filter((s) => s.shift.status !== ShiftStatusConst.CANCELLED);

    const openStatuses: ShiftStatusConst[] = [ShiftStatusConst.AVAILABLE, ShiftStatusConst.OFFERED];
    const todayOpen = todayActive.filter((s) => openStatuses.includes(s.shift.status));

    this.nextShifts = shiftsWithTimes
      .filter((s) => s.start >= now)
      .sort((a, b) => a.start.getTime() - b.start.getTime())
      .slice(0, 6)
      .map(({ shift, start, end }) => ({
        start,
        end,
        hospital: shift.hospital?.name,
        department: shift.department?.name,
        professional: shift.assignedTo?.name || shift.professional?.name,
        status: shift.status,
      }));

    this.alerts = this.buildAlerts(todayOpen, today, now);
  }

  private buildAlerts(
    todayOpen: Array<{ shift: IShift; start: Date; end: Date }>,
    todayAll: Array<{ shift: IShift; start: Date; end: Date }>,
    now: Date
  ): Array<{ title: string; description: string; level: 'info' | 'warning' | 'danger' }> {
    const alerts: Array<{ title: string; description: string; level: 'info' | 'warning' | 'danger' }> = [];

    const openSoon = todayOpen
      .filter((s) => s.start.getTime() - now.getTime() <= 4 * 60 * 60 * 1000)
      .sort((a, b) => a.start.getTime() - b.start.getTime());

    for (const item of openSoon.slice(0, 3)) {
      const place = [item.shift.hospital?.name, item.shift.department?.name].filter(Boolean).join(' • ');
      alerts.push({
        title: 'Plantão sem cobertura',
        description: `${this.timeRange(item.start, item.end)}${place ? ' • ' + place : ''}`,
        level: 'danger',
      });
    }

    const endingSoon = todayAll
      .filter((s) => s.shift.status === ShiftStatusConst.IN_PROGRESS)
      .filter((s) => s.end.getTime() - now.getTime() <= 60 * 60 * 1000)
      .sort((a, b) => a.end.getTime() - b.end.getTime())
      .slice(0, 2);

    for (const item of endingSoon) {
      const who = item.shift.assignedTo?.name || item.shift.professional?.name;
      alerts.push({
        title: 'Plantão finalizando',
        description: `${this.timeRange(item.start, item.end)}${who ? ' • ' + who : ''}`,
        level: 'warning',
      });
    }

    if (this.stats.noShow > 0) {
      alerts.push({
        title: 'Faltas registradas',
        description: `${this.stats.noShow} plantão(ões) marcados como falta hoje.`,
        level: 'danger',
      });
    }

    if (alerts.length === 0) {
      alerts.push({
        title: 'Tudo certo por enquanto',
        description: 'Sem alertas críticos para hoje.',
        level: 'info',
      });
    }

    return alerts.slice(0, 6);
  }

  private shiftStartDateTime(shift: IShift): Date {
    const date = this.asDate(shift.startDate);
    const [h, m] = (shift.startTime || '00:00').split(':').map((v) => Number(v));
    const dt = new Date(date);
    dt.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
    return dt;
  }

  private shiftEndDateTime(shift: IShift): Date {
    const date = this.asDate((shift as any).endDate || shift.startDate);
    const [h, m] = (shift.endTime || '00:00').split(':').map((v) => Number(v));
    const dt = new Date(date);
    dt.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
    return dt;
  }

  private asDate(value: unknown): Date {
    return value instanceof Date ? value : new Date(value as any);
  }

  private timeRange(start: Date, end: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(start.getHours())}:${pad(start.getMinutes())}–${pad(end.getHours())}:${pad(end.getMinutes())}`;
  }

  private formatDateForApi(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  private addDays(date: Date, days: number): Date {
    const dt = new Date(date);
    dt.setDate(dt.getDate() + days);
    return dt;
  }

  private dayKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
